import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock, Phone, User } from 'lucide-react';
import { Card } from '../ui/Card.jsx';
import { SourceChip } from './SourceChip.jsx';
import { InlineAudioPlayer } from './InlineAudioPlayer.jsx';
import { fetchSessionDetail } from '../../api/survey.js';
import { overlayDismiss } from '../../lib/overlayDismiss';

/**
 * SurveySessionDetailModal — 單一 session 詳情（彈窗，不開新 route）
 *
 * props:
 *   open, onClose
 *   avatarId, sessionId, hostId
 */
export function SurveySessionDetailModal({ open, onClose, avatarId, sessionId, hostId }) {
    const { t } = useTranslation('survey');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !avatarId || !sessionId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setData(null);
        (async () => {
            try {
                const r = await fetchSessionDetail(avatarId, sessionId, { hostId });
                if (!cancelled) setData(r);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, avatarId, sessionId, hostId]);

    if (!open) return null;

    const session = data?.session || {};
    const responses = data?.responses || [];
    const transcript = data?.transcript || [];
    const recordingUrl = data?.recordingUrl || null;

    const displayId = session.phone
        || (session.source === 'preview'
            ? t('previewId', { id: (session.userId || sessionId).slice(0, 8) })
            : t('detail.respondent'));
    const subtitle = session.source === 'preview'
        ? t('source.previewCall')
        : (session.campaignId ? t('detail.campaignRespondent') : t('detail.singleRespondent'));

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-6xl h-[88vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-cream-200 shrink-0">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400">{t('detail.title')}</div>
                        <h2 className="heading-display text-base font-semibold text-ink-900">
                            {displayId} <span className="ml-2 text-xs font-normal text-ink-500">· {subtitle}</span>
                        </h2>
                    </div>
                    <button onClick={onClose} className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition">
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto p-5">
                    {loading && <div className="text-sm text-ink-400 italic">{t('common:state.loading')}</div>}
                    {error && <div className="text-sm text-danger-500">{error.message}</div>}
                    {data && (
                        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-5">
                            {/* 左側 */}
                            <div className="space-y-3">
                                <Card>
                                    <div className="space-y-3">
                                        <InfoRow icon={Phone} label={t('detail.phoneId')}>
                                            {displayId}
                                            <SourceChipWrap source={session.source} />
                                        </InfoRow>
                                        <InfoRow icon={Clock} label={t('detail.startEnd')}>
                                            <div>{session.startedAt ? new Date(session.startedAt).toLocaleString('zh-TW') : '—'}</div>
                                            <div className="mono-caption">{t('duration.label')} {formatDuration(session.durationSec, t)}</div>
                                        </InfoRow>
                                        <InfoRow icon={User} label={t('detail.progress')}>
                                            <span className={['text-base font-medium', session.completed ? 'text-emerald-600' : 'text-ink-900'].join(' ')}>
                                                {session.answeredCount} / {session.totalQuestions}
                                            </span>
                                            {session.completed && <span className="ml-2 text-xs text-emerald-600">{t('completedMark')}</span>}
                                        </InfoRow>
                                    </div>
                                </Card>

                                {session.demographics && Object.keys(session.demographics).length > 0 && (
                                    <Card>
                                        <div className="mono-caption mb-2">{t('detail.demographics')}</div>
                                        <dl className="space-y-1.5">
                                            {Object.entries(session.demographics).map(([k, v]) => (
                                                <div key={k} className="flex justify-between text-sm">
                                                    <dt className="text-ink-500">{k}</dt>
                                                    <dd className="text-ink-900 font-medium">{String(v)}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                    </Card>
                                )}

                                {recordingUrl && (
                                    <Card>
                                        <div className="mono-caption mb-2">{t('detail.recording')}</div>
                                        <InlineAudioPlayer
                                            src={recordingUrl}
                                            refresh={() => fetchSessionDetail(avatarId, sessionId, { hostId }).then((r) => r?.recordingUrl || null)}
                                        />
                                    </Card>
                                )}
                            </div>

                            {/* 右側 */}
                            <div className="space-y-5">
                                <Card>
                                    <div className="mono-caption mb-3">{t('detail.answersTitle', { n: responses.length })}</div>
                                    <div className="space-y-3">
                                        {responses
                                            .slice()
                                            .sort((a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0))
                                            .map((r) => (
                                                <ResponseRow key={r.id || `${r.sessionId}_${r.questionId}`} response={r} />
                                            ))}
                                    </div>
                                </Card>

                                {transcript.length > 0 && (
                                    <Card>
                                        <div className="mono-caption mb-3">{t('detail.transcriptTitle', { n: transcript.length })}</div>
                                        <div className="space-y-1.5">
                                            {transcript.map((t, i) => (
                                                <TranscriptRow key={i} role={t.role} text={t.text} />
                                            ))}
                                        </div>
                                    </Card>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function InfoRow({ icon: Icon, label, children }) {
    return (
        <div className="flex gap-3">
            <div className="shrink-0 w-5 pt-0.5 text-ink-400">{Icon && <Icon size={14} />}</div>
            <div className="flex-1 min-w-0">
                <div className="mono-caption mb-0.5">{label}</div>
                <div className="text-sm text-ink-900">{children}</div>
            </div>
        </div>
    );
}

function SourceChipWrap({ source }) {
    return <span className="ml-2"><SourceChip source={source} compact /></span>;
}

function ResponseRow({ response: r }) {
    const { t } = useTranslation('survey');
    const order = (r.questionOrder ?? 0) + 1;
    const label = Array.isArray(r.answerLabel) ? r.answerLabel.join(', ') : (r.answerLabel || r.answer || '—');
    return (
        <div className="pl-3 border-l-2 border-cream-200">
            <div className="text-sm font-medium text-ink-900">Q{order}. {r.questionText}</div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                <span className="text-sm text-accent-700">{t('detail.answer')}{label}</span>
                {r.rawAnswer && r.rawAnswer !== label && (
                    <span className="text-xs text-ink-400">{t('detail.rawAnswer', { raw: r.rawAnswer })}</span>
                )}
                {r.demographicField && (
                    <span className="text-[11px] mono-caption">→ demo.{r.demographicField}</span>
                )}
            </div>
        </div>
    );
}

function TranscriptRow({ role, text }) {
    const meta = {
        user: { label: 'user', cls: 'text-accent-700' },
        agent: { label: 'ai', cls: 'text-ink-400' },
    }[role] || { label: role || '—', cls: 'text-ink-500' };
    return (
        <div className="flex gap-3 text-sm">
            <span className={['shrink-0 w-12 font-mono text-[10px] uppercase tracking-wide mt-0.5', meta.cls].join(' ')}>
                {meta.label}
            </span>
            <span className="text-ink-700">{text}</span>
        </div>
    );
}

function formatDuration(sec, t) {
    if (sec == null || sec < 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return t('duration.value', { m, s: String(s).padStart(2, '0') });
}
